import { useAuth } from "@/context/auth";
import useDashboardAccess from "@/hooks/useDashboardAccess";
import { isoDate } from "@/modules/mobileHome/format";
import { invoiceSuggestions } from "@/modules/mobileHome/invoiceSuggestion";
import { appointmentService } from "@/services/appointment";
import { useGetMerchantForMerchantUser } from "@/services/merchant/utilities";
import { Text, UnstyledButton } from "@mantine/core";
import { IconCalendarPlus, IconChevronLeft, IconFilePlus, IconMessagePlus, IconUserPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import CustomDrawer from "../drawer";
import { CreateSheetBody } from "./style";

const createActions = [
  { to: "/dashboard/calendar", label: "نوبت جدید", hint: "رزرو وقت برای مشتری", icon: IconCalendarPlus, tone: "sales" },
  { to: "/dashboard/payments/add", label: "ثبت فاکتور", hint: "بعد از انجام خدمت", icon: IconFilePlus, tone: "sales" },
  { to: "/dashboard/customers/add", label: "مشتری جدید", hint: "افزودن به فهرست مشتریان", icon: IconUserPlus, tone: "people" },
  { to: "/dashboard/sendMessage", label: "ارسال پیامک", hint: "به همه یا گروهی از مشتریان", icon: IconMessagePlus, tone: "people" },
];

type Props = {
  opened: boolean;
  close: () => void;
  goTo: (to: string) => void;
};

export default function CreateSheet({ opened, close, goTo }: Props) {
  const { isManager, isStylist } = useDashboardAccess();
  const phone = useAuth().getUserByToken()?.phoneNumber ?? "";
  const memberId = useGetMerchantForMerchantUser(phone).data?.id;
  const today = isoDate(new Date());
  // Fetched when the sheet opens, so an invoice registered a minute ago is already left out.
  const visits = useQuery({
    queryKey: ["getDailyAppointment", today, today],
    queryFn: () => appointmentService.getDailyAppointment(today, today),
    enabled: opened && (isManager || Boolean(memberId)),
    staleTime: 0,
  });
  const suggestions = useMemo(() => {
    if (!opened || !visits.data) return [];
    if (isManager) return invoiceSuggestions(visits.data, { withStaff: true });
    return isStylist && memberId ? invoiceSuggestions(visits.data, { merchantUserId: memberId }) : [];
  }, [opened, visits.data, isManager, isStylist, memberId]);

  return (
    <CustomDrawer
      opened={opened}
      onClose={close}
      close={close}
      position="bottom"
      custom="create"
      radius="md"
      // Mantine has no "auto" drawer size; the sheet is only as tall as its actions.
      styles={{ content: { height: "auto", maxHeight: "calc(100% - 40px)" } }}
      title={<Text fw={800}>ایجاد سریع</Text>}
    >
      <CreateSheetBody>
        {suggestions.length > 0 && (
          <div className="create-suggestions">
            {suggestions.map((suggestion) => (
              <UnstyledButton
                key={suggestion.appointmentId}
                className="create-suggestion"
                onClick={() => goTo(`/dashboard/payments/add/${suggestion.appointmentId}`)}
              >
                <span className="create-suggestion-icon">
                  <IconFilePlus size={22} stroke={1.7} aria-hidden />
                </span>
                <span className="create-suggestion-text">
                  <strong>فاکتور برای {suggestion.customer}</strong>
                  <span>{suggestion.detail}</span>
                </span>
                <IconChevronLeft size={18} stroke={1.7} aria-hidden />
              </UnstyledButton>
            ))}
          </div>
        )}
        <div className="create-grid">
          {createActions.map((action) => (
            <UnstyledButton key={action.to} className="create-action" data-tone={action.tone} onClick={() => goTo(action.to)}>
              <span className="create-icon">
                <action.icon size={24} stroke={1.7} aria-hidden />
              </span>
              <span className="create-text">
                <strong>{action.label}</strong>
                <span>{action.hint}</span>
              </span>
            </UnstyledButton>
          ))}
        </div>
      </CreateSheetBody>
    </CustomDrawer>
  );
}
